import { Component, Input, LOCALE_ID, Inject, ElementRef, ViewChild, ChangeDetectorRef } from "@angular/core";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CvNumberLocalizePipe } from "src/app/reusables/pipes/cv-number-localize.pipe";
import { GameObject, GameType, RankingByLogin, TooltipType } from "../game.models";
import { GenericMetricFormatterPipe } from "src/app/reusables/pipes/generic-metric-formatter.pipe";
import { TooltipDirective } from "ngx-bootstrap/tooltip";

@Component({
    selector: 'cxone-game-multiplier-madness',
    templateUrl: './cxone-game-multiplier-madness.component.html',
    styleUrl: './cxone-game-multiplier-madness.component.scss'
})
export class CxoneGameMultiplierMadnessComponent {
    @Input() game: GameObject;
    @Input() gameStatus: string;
    @Input() rankingData: RankingByLogin;

    @ViewChild('qualifierText') qualifierText: ElementRef<HTMLElement>;
    @ViewChild('metricText', { static: false }) metricText: ElementRef;
    @ViewChild('tooltip', { static: false }) tooltip!: TooltipDirective;
    @ViewChild('tooltipQualifier', { static: false }) tooltipQualifier!: TooltipDirective;   

    DateFormat = "MMM d, y";
    isTextTruncated = false;
    QualifierIcon: string = "";
    QualifierStatus: boolean = false;
    QualifierValidationLeft: string = "";
    QualifierValidationRight: string = "";
    value: string = "";
    badgetAvailableGames: GameType[];
    qualifierAvailableGames: GameType[];
    metricAvailableGames: GameType[];
    GameType = GameType; 
    tooltipValidation = false; 
    iconMap: { [key: string]: string } = 
        {
            "completed": `${window['_BaseURI']}/App_Themes/CXone/icons/gamification/games/check.svg#check`,
            "failed": `${window['_BaseURI']}/App_Themes/CXone/icons/gamification/games/fail.svg#fail`
        };
    TooltipType = TooltipType;
    constructor(
        @Inject(LOCALE_ID) private locale: string,
        public changeDetector: ChangeDetectorRef,
        public cvLocalizationService: CVLocalizationService,
        public cvNumberLocalizePipe: CvNumberLocalizePipe,
        public metricFormatter: GenericMetricFormatterPipe,
    ) { }

    async ngOnInit() {
        await this.loadLocalization();
        this.badgetAvailableGames = [
            GameType.TopDog,
            GameType.Bullseye,
            GameType.Percentile,
            GameType.Milestone,
            GameType.Connect3,
            GameType.Blackout,
            GameType.TopTeam
          ];
        this.qualifierAvailableGames = [GameType.MultiplierMadness];
        this.metricAvailableGames = [GameType.MultiplierMadness];
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnDestroy(){}
    
    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('Games');
        }
        catch {
            console.error('Failed to load localization for Games');
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Games", key, defaultValue);
        return baseString;
    }

    checkTextTruncation(tooltip: string, type: TooltipType): string {
        this.tooltipValidation = true;
        switch (type) {
            case TooltipType.Q1:
                if (this.metricText) {
                    return this.metricText.nativeElement.scrollWidth != this.metricText.nativeElement.clientWidth ? tooltip : "";
                }
                break;
            case TooltipType.Q2:
                if (this.qualifierText) {
                    return this.qualifierText.nativeElement.scrollWidth != this.qualifierText.nativeElement.clientWidth ? tooltip : "";
                }
                break;        
            default: this.tooltipValidation = false;
        }
        return "";
    }

    getMetricValue() {
        let conversionType = this.game?.SelectedMetrics[0]?.conversionType;
        let myScore = this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos[0]?.MyScore;
        if (myScore === undefined) {
            myScore = this.rankingData?.gameRewards?.MyRankingObj?.Score;
        }
        if (myScore === undefined || this.gameStatus === 'Upcoming') {
            return '--';
        }
        return this.getFormattedData(myScore, conversionType);
    }

    getQualifierValidation() {
        this.value = this.game?.Qualifier?.abbrev || "";
        let conversionType = this.game?.SelectedMetrics[0]?.conversionType;
        let MetricGroupInfos = this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos;
        const qualifierValue = MetricGroupInfos
            ? this.getFormattedData(MetricGroupInfos[MetricGroupInfos?.length - 1]?.MyMetricValue, conversionType)
            : 0;
        const comparerType = this.game.QualifierLevels?.comparerType;
        let leftNumber = this.game?.QualifierLevels?.leftNumber;
        let rightNumber = this.game?.QualifierLevels?.rightNumber;
        if (comparerType === 3 && leftNumber > rightNumber) {
            [rightNumber, leftNumber] = [leftNumber, rightNumber];
        }
        this.evaluateQualifierStatus(comparerType, leftNumber, qualifierValue, rightNumber);
        this.getQualifierIcon();
        return this.gameStatus !== 'Upcoming';
    }

    public evaluateQualifierStatus(comparerType: number, leftNumber: number, qualifierValue: any, rightNumber?: number): void {
        let conversionType = this.game?.Qualifier?.conversionType;
        let convertedNumber = this.getFormattedData(leftNumber, conversionType);
        let convertedNumberRight = this.getFormattedData(rightNumber, conversionType);
        switch (comparerType) {
            case 1:
                this.QualifierStatus = (qualifierValue > leftNumber);
                this.QualifierValidationRight = `> ${convertedNumber}`;
                break;
            case 2:
                this.QualifierStatus = (qualifierValue < leftNumber);
                this.QualifierValidationRight = `< ${convertedNumber}`;
                break;
            case 3:
                this.QualifierStatus = (qualifierValue > leftNumber && qualifierValue < rightNumber);
                this.QualifierValidationLeft = `${convertedNumber} <`;
                this.QualifierValidationRight = `< ${convertedNumberRight}`;
                break;
            default:
                break;
        }
    }

    getQualifierIcon() {
        const imageKey = this.QualifierStatus ? "completed" : "failed"
        this.QualifierIcon = this.iconMap[imageKey] || '';
    }

    getFormattedData(value, conversionType): string {
        return this.metricFormatter.transform(value, conversionType);
    }

    showItemValidation(option: number, type: string): boolean {
        const typeMapping = {
            'badget': this.badgetAvailableGames,
            'metric': this.metricAvailableGames,
            'qualifier': this.qualifierAvailableGames
        };
    
        return typeMapping[type]?.includes(option) || false;
    }    

    public getLocalizaCoins(value): string {
        let coinText = this.getLocalizationString("Coins", "Coins")
        if (value === 1) {
            coinText = this.getLocalizationString("Coin", "Coin")
        }
        return coinText.toLowerCase();
    }
}
